import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { URLS } from "../../../../services/app-urls";
import { BaseService } from "../../../../services/base-service";
import { ErrorComponent } from "../../../../components/error/error.component";
import { AnalysisPriority } from "../../../../models/general/analysis-priority";
import DraggableTable from "../../../../components/table/table-dragdrop-line.component";
import { Box, Button, Checkbox, Chip, Fab, Grid, Paper, TableCell, TextField, Typography } from "@mui/material";
import { translate } from "../../../../translate/translate";
import WarningTooltip from "../../../../components/tooltip/warning-tooltip";
import { ArrowUpward, Info, Search } from "@mui/icons-material";
import Loading from "../../../../../app/components/loading/loading";
import { Field, Form, Formik } from "formik";


const AnalysisPriorityPageComponent = ({ forecastId, handleSelectPriority }: any) => {
  const service = new BaseService<AnalysisPriority>(URLS.ANALYSIS_PRIORITY);
  const [data, setData] = useState<AnalysisPriority[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [searchBOM, setSearchBOM] = useState("");

  const initializedRef = useRef(false);

  useEffect(() => {
    fetchForecastComponents();
    setSearchBOM(""); // Reset search when forecastId changes
  }, [forecastId]);

  useEffect(() => {
    if (data.length > 0 && !initializedRef.current) {
      initializedRef.current = true;

      const initiallySelected = data
        .filter((item) => !item.is_empty)
        .map((item) => item.id);

      setSelectedRows(initiallySelected);

      const empty = data.filter((item) => item.is_empty);
      const selectedItems = data.filter((item) =>
        initiallySelected.includes(item.id),
      );

      handleSelectPriority(selectedItems, empty);
    }
  }, [data]);

  useEffect(() => {
    sendPriorities();
  }, [data, selectedRows]);

  const fetchForecastComponents = () => {
    if (!forecastId) return;
    service.clearParameters();
    service.addParameter("forecast_id", forecastId);
    service
      .getAll()
      .then((response: any) => {
        if (response.data.length === 0) {
          updatePriorities([]);
          return;
        }
        updatePriorities(response.data);
      })
      .catch(ErrorComponent)
      .finally(() => {
        setLoading(false);
      });
  };

  const updatePriorities = (rows: AnalysisPriority[]) => {
    const updatedRows = rows.map((item, index) => ({
      ...item,
      priority: index + 1,
    }));
    setData(updatedRows);
  };

  const sendPriorities = () => {
    const selected = data.filter((item) => selectedRows?.includes(item.id));
    const empty = data
      .filter((item) => item.is_empty);
    handleSelectPriority(selected, empty);
  };

  const handleReorder = (newRows: AnalysisPriority[]) => {
    updatePriorities(newRows);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newRows = [...data];
    [newRows[index - 1], newRows[index]] = [newRows[index], newRows[index - 1]];
    updatePriorities(newRows);
  };

  const handleMoveDown = (index: number) => {
    if (index === data.length - 1) return;
    const newRows = [...data];
    [newRows[index], newRows[index + 1]] = [newRows[index + 1], newRows[index]];
    updatePriorities(newRows);
  };

  const handleIconBomEmpty = (hasEmpty: boolean) => {
    return hasEmpty && <WarningTooltip messageKey={"bom_not_imported"} />;
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const selectableIds = data.filter((row) => !row.is_empty).map((row) => row.id);
      setSelectedRows(selectableIds);
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id: string, checked: boolean) => {
    setSelectedRows((prev) => {
      return checked ? [...prev, id] : prev.filter((rowId) => rowId !== id);
    });
  };

  const renderCellHandler = (column: string, index: number, row: any) => {
    if (column === "bom_number") {
      return (
        <TableCell key={index}>
          <Box display="flex" alignItems="center">
            <Typography variant="body1">{row.bom_number}</Typography>
            {handleIconBomEmpty(row.is_empty)}
          </Box>
        </TableCell>
      );
    }
    if (column === "__checkbox") {
      return (
        <TableCell key={`checkbox-${row.id}`}>
          <Checkbox
            checked={selectedRows.includes(row.id)}
            onChange={(e) => handleSelectRow(row.id, e.target.checked)}
            disabled={row.is_empty}
          />
        </TableCell>
      );
    }
    return null;
  };

  const renderHeaderCellHandler = (column: string) => {
    if (column === "__checkbox") {
      const selectableRows = data.filter((row) => !row.is_empty);
      const allRowsDisabled = selectableRows.length === 0;

      const allSelected =
        selectableRows.length > 0 &&
        selectableRows.every((row) => selectedRows.includes(row.id));

      return (
        <Checkbox
          checked={allSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
          disabled={allRowsDisabled}
        />
      );
    }

    return translate.t(column);
  };

  const columns = [
    { field: "__checkbox", headerName: "", width: 50, minWidth: 50 },
    { field: "priority", headerName: "order", width: 100, minWidth: 100 },
    { field: "bom_number", headerName: "model", width: 100, minWidth: 100 },
    { field: "final_product_description", headerName: "description", width: 200, minWidth: 200 },
    { field: "modified_at", headerName: "imported_at", width: 200, minWidth: 200 },
  ];

  const isRowEmpty = (row: any) => {
    return row.is_empty;
  };

  const headerCellCustomStyleHandler = (column?: string) => {
    if (column === "__checkbox") {
      return {
        backgroundColor: "#FFFF !important",
        color: "var(--primary-main) !important",
        borderBottom: "1px solid var(--primary-main)",
        borderTop: "1px solid var(--primary-main)",
      };
    }
    return {
      backgroundColor: "#FFFF !important",
      color: "var(--primary-main) !important",
      borderBottom: "1px solid var(--primary-main)",
      borderTop: "1px solid var(--primary-main)",
      padding: "8px",
    };
  };

  const handleMoveSelectedToTop = () => {
    if (!selectedRows.length) return;

    const newRows = [...data];
    const selectedSet = new Set(selectedRows);

    const selectedItems = newRows.filter(row => selectedSet.has(row.id));
    const unselectedItems = newRows.filter(row => !selectedSet.has(row.id));

    updatePriorities([...selectedItems, ...unselectedItems]);
    setSearchBOM("");
  };

  const filterData = () => {
    if (!searchBOM.trim()) {
      return data;
    }

    const searchTerm = searchBOM.toLowerCase();
    return data.filter((item) => {
      const bomNumber = item.bom_number?.toLowerCase() || "";
      const description = item.description?.toLowerCase() || "";
      const priority = item.priority?.toString() || "";

      return (
        bomNumber.includes(searchTerm) ||
        description.includes(searchTerm) ||
        priority.includes(searchTerm)
      );
    });
  };

  const filteredData = filterData();

  return (
    <section>
      <Box sx={{ display: "flex", alignItems: "center", marginTop: 1, mb: 1.0 }}>
        <Typography sx={{ fontSize: 12, color: "var(--default-color)", fontWeight: 500 }}>
          {translate.t("order_button")}
        </Typography>
      </Box>

      <Loading open={loading} />

      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <Box p={2}>
            <Formik
              enableReinitialize={true}
              initialValues={{ bom_number: "" }}
              onSubmit={() => { }}
            >
              {() => (
                <Form autoComplete="off" >
                  <Grid alignItems="center" container spacing={2} direction={"row"}>
                    <Grid item xs>
                      <Field
                        as={TextField}
                        name="bom_number"
                        label={[translate.t("model")]}
                        fullWidth
                        variant={"outlined"}
                        value={searchBOM}
                        onChange={(event: ChangeEvent<{ value: unknown }>) => setSearchBOM(event.target.value as string)}
                      />
                    </Grid>
                    <Grid item>
                      <Fab size="small" type="submit" color="primary">
                        <Search />
                      </Fab>
                    </Grid>

                    {selectedRows.length > 0 && (
                      <>
                        <Grid item>
                          <Box
                            sx={{
                              width: '1px',
                              height: 32,
                              bgcolor: 'divider',
                              mx: 1,
                            }}
                          />
                        </Grid>

                        <Grid item>
                          <Chip
                            label={`${selectedRows.length} ${translate.t("selected_models")}`}
                            variant="outlined"
                            sx={{
                              height: 36,
                              borderRadius: 2,
                              bgcolor: '#FFF4E5',
                              borderColor: '#FFD59E',
                              color: '#8A5A00',
                              fontWeight: 500,
                              '& .MuiChip-label': {
                                px: 2,
                                fontSize: 14,
                                lineHeight: '36px',
                              },
                            }}
                          />
                        </Grid>
                      </>
                    )}
                    {selectedRows.length > 0 && (
                      <Grid item>
                        <Button
                          variant="outlined"
                          startIcon={<ArrowUpward />}
                          onClick={handleMoveSelectedToTop}
                        >
                          {translate.t("move_top")}
                        </Button>
                      </Grid>
                    )}

                  </Grid>
                </Form>
              )}
            </Formik>
          </Box>
        </Paper>
      </Grid>

      <DraggableTable
        columns={columns}
        headerCellCustomStyle={headerCellCustomStyleHandler}
        rows={filteredData}
        rowKey="id"
        stickyHeader={true}
        onReorder={handleReorder}
        onMoveUp={handleMoveUp}
        onMoveDown={handleMoveDown}
        height={650}
        dateColumns={["modified_at"]}
        renderCellHandler={{ bom_number: renderCellHandler, __checkbox: renderCellHandler }}
        renderHeaderCellHandler={{ __checkbox: renderHeaderCellHandler }}
        headerCheckbox={{
          allSelected: selectedRows.length === filteredData.length && filteredData.length > 0,
          onSelectAll: handleSelectAll,
        }}
        isRowDraggable={isRowEmpty}
        isArrowsDisabled={isRowEmpty}
      />
      {
        filteredData.length && !loading ? (
          <Box sx={{ display: "flex", alignItems: "center", marginTop: 2, }}>
            <Info />
            <Typography sx={{ marginLeft: 1, fontSize: 12, color: "var(--default-color)", fontWeight: 500 }}>
              {translate.t("change_priority")}
            </Typography>
          </Box>
        ) : null
      }

    </section>
  );
};

export default AnalysisPriorityPageComponent;
